import {
  RequiredIndicatorPlacement as Placement,
  RequiredIndicator,
} from '@wix/ambassador-forms-v4-form/types';
import {
  type Node,
  Node_Type,
  type RichContent,
  type WixRicosTheme,
} from '@wix/ricos';
import { type Value } from '@wix/ricos/dist/src/ricos-schema/legacy/wix/rich_content/v1/struct';
import cloneDeep from 'lodash/cloneDeep';
import React, { useMemo } from 'react';

import { useConfig } from '../../contexts/config-context';
import { useFieldPropsV2 } from '../../contexts/field-props-context';
import { useTranslation } from '../../contexts/translation-context';
import { type TFunction } from '../../contexts/translation-context';
import { type InputFieldProps } from '../../types/field-props';
import { type PossibleValues } from '../form/types';
import { htmlAttributesPlugin } from '../ricos-viewer/ricos-html-attributes-plugin';

type RichLabelChildrenProps = {
  content: RichContent;
  locale?: string;
  htmlAttributesPlugin: any;
};

type RichLabelProps = {
  className?: string;
  requiredClassName?: string;
  theme?: WixRicosTheme;
  children: (props: RichLabelChildrenProps) => React.ReactNode;
};

export type RichLabelInternalProps = {
  requiredIndicatorHidden?: boolean;
};

const createIndicatorNode = (
  requiredIndicatorType: RequiredIndicator,
  t: TFunction,
  className?: string,
): Node => {
  const indicator =
    requiredIndicatorType === RequiredIndicator.TEXT
      ? t('settings.required-indicator-text')
      : '*';

  const str = (v: string) => ({ stringValue: v } as Value);
  const fields: Record<string, Value> = {
    'aria-hidden': str('true'),
  };

  if (className) {
    fields.className = str(className);
  }

  return {
    type: Node_Type.EXTERNAL,
    id: '',
    nodes: [],
    externalData: {
      fields,
    },
    textData: {
      text: indicator,
      decorations: [],
    },
  };
};

const injectRequiredIndicator = (
  label: RichContent,
  placement: Placement,
  indicatorNode: Node,
): RichContent => {
  const result = cloneDeep(label);

  switch (placement) {
    case Placement.BEFORE_FIELD_TITLE:
      const firstNode = result?.nodes.find(Boolean);
      if (firstNode) {
        firstNode.nodes ??= [];
        firstNode.nodes.unshift(indicatorNode);
      }
      break;
    case Placement.AFTER_FIELD_TITLE:
      const lastNode = result?.nodes?.findLast(Boolean);
      if (lastNode) {
        lastNode.nodes ??= [];
        lastNode.nodes.push(indicatorNode);
      }
      break;
  }

  return result;
};

export const RichTextLabel = ({
  className,
  requiredClassName,
  theme,
  children,
  ...internal
}: RichLabelProps) => {
  const { requiredIndicatorHidden = false } =
    internal as RichLabelInternalProps;
  const fieldProps = useFieldPropsV2();
  const {
    label,
    required = false,
    requiredIndicatorType = RequiredIndicator.ASTERISK,
    requiredIndicatorPlacement = Placement.AFTER_FIELD_TITLE,
  } = fieldProps as InputFieldProps<PossibleValues, RichContent>;

  const { t } = useTranslation();
  const { language } = useConfig();

  const labelWithIndicator = useMemo(() => {
    if (!required || requiredIndicatorHidden || !label) {
      return label;
    }

    const indicatorNode = createIndicatorNode(
      requiredIndicatorType,
      t,
      requiredClassName,
    );
    return injectRequiredIndicator(
      label,
      requiredIndicatorPlacement,
      indicatorNode,
    );
  }, [
    required,
    requiredIndicatorHidden,
    label,
    requiredIndicatorType,
    requiredIndicatorPlacement,
    t,
    requiredClassName,
  ]);

  return (
    <div className={className}>
      {children({
        content: labelWithIndicator as RichContent,
        locale: language,
        htmlAttributesPlugin,
      })}
    </div>
  );
};
